import React, { useState, useEffect } from 'react';
import { MessageCircle, Users, RefreshCw, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/contexts/unified-permissions';
import { useChatParticipants } from '../hooks/useChatParticipants';
import { useChatOperations } from '../hooks/useChatOperations';
import { ChatWindow } from './ChatWindow';
import { UserPicker } from './UserPicker';

import type { AdminChatPanelProps, ChatParticipant } from '../types';
import { formatDistanceToNow } from 'date-fns';

/**
 * Admin chat panel component
 * Shows list of users who have sent messages and allows admin to chat with them
 */
export const AdminChatPanel: React.FC<AdminChatPanelProps> = ({ className }) => {
  const { user, isAdmin } = usePermissions();
  const { participants, loading, error, refetch } = useChatParticipants();
  const { markMessagesAsRead, deleteConversation, clearConversation, loading: operationsLoading } = useChatOperations();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);

  // Auto-mark messages as read when a conversation is selected
  useEffect(() => {
    if (selectedUserId && user?.id && isAdmin) {
      // Mark messages from the selected user as read
      markMessagesAsRead(selectedUserId);
    }
  }, [selectedUserId, user?.id, isAdmin, markMessagesAsRead]);

  // Only show for admin users
  if (!user || !isAdmin) {
    return (
      <Alert>
        <AlertDescription>
          Access denied. This panel is only available to administrators.
        </AlertDescription>
      </Alert>
    );
  }

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
    setSelectedUserId(null);
    // Refresh participants to update unread counts
    refetch();
  };

  // Handle conversation management actions
  const handleDeleteConversation = async (otherUserId: string) => {
    const success = await deleteConversation(otherUserId);
    if (success) {
      if (selectedUserId === otherUserId) {
        setIsChatOpen(false);
        setSelectedUserId(null);
      }
      // Small delay to ensure database operation completes before refresh
      setTimeout(() => {
        refetch(); // Refresh the participants list
      }, 500);
    }
  };

  const handleClearConversation = async (otherUserId: string) => {
    const success = await clearConversation(otherUserId);
    if (success) {
      // Small delay to ensure database operation completes before refresh
      setTimeout(() => {
        refetch(); // Refresh the participants list to update badge counts
      }, 500);
    }
  };

  const selectedUser = participants.find(p => p.id === selectedUserId);

  return (
    <div className={cn('space-y-6', className)}>


      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-6 w-6" />
          <div>
            <h2 className="text-2xl font-bold">Chat Dashboard</h2>
            <p className="text-muted-foreground">
              Manage conversations with users
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <UserPicker onUserSelect={handleUserSelect} />
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load chat participants: {error.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading state */}
      {loading && !participants.length && (
        <LoadingState description="Loading chat participants..." />
      )}

      {/* Empty state */}
      {!loading && !error && participants.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
            <p className="text-muted-foreground text-center">
              Users who send messages will appear here. You can then chat with them directly.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Participants list */}
      {participants.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Active Conversations ({participants.length})
            </CardTitle>
            <CardDescription>
              Click on a user to start or continue a conversation
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              <div className="space-y-1 p-4">
                {participants.map((participant) => (
                  <ParticipantCard
                    key={participant.id}
                    participant={participant}
                    onSelect={handleUserSelect}
                    onClear={handleClearConversation}
                    onDelete={handleDeleteConversation}
                    operationsLoading={operationsLoading}
                  />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Chat window */}
      {selectedUserId && selectedUser && (
        <ChatWindow
          isOpen={isChatOpen}
          onClose={handleChatClose}
          otherUserId={selectedUserId}
          otherUserName={selectedUser.fullName || selectedUser.username || 'User'}
        />
      )}
    </div>
  );
};

/**
 * Individual participant card component
 */
interface ParticipantCardProps {
  participant: ChatParticipant;
  onSelect: (userId: string) => void;
  onClear?: (userId: string) => void;
  onDelete?: (userId: string) => void;
  operationsLoading?: boolean;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  onSelect,
  onClear,
  onDelete,
  operationsLoading = false
}) => {
  const displayName = participant.fullName || participant.username || 'Unknown User';
  const hasUnread = participant.unreadCount > 0;

  const handleClick = () => {
    onSelect(participant.id);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.(participant.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(participant.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors group',
        'hover:bg-muted/50',
        hasUnread && 'bg-muted/30 border-primary/20'
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Avatar placeholder */}
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary">
            {displayName.charAt(0).toUpperCase()}
          </span>
        </div>

        {/* User info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{displayName}</p>
            {hasUnread && (
              <Badge variant="destructive" className="h-5 min-w-[20px] text-xs px-1.5">
                {participant.unreadCount > 99 ? '99+' : participant.unreadCount}
              </Badge>
            )}
          </div>
          {participant.lastActivity && (
            <p className="text-xs text-muted-foreground">
              Last active {formatDistanceToNow(participant.lastActivity, { addSuffix: true })}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons - show on hover */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={operationsLoading}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            title="Clear messages"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={operationsLoading}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            title="Remove from list"
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
        )}
        <MessageCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-1" />
      </div>
    </div>
  );
};
